import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Ad, EditAd, Media, MediaFolder, Picture } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import { Input, Label, Radio, Textarea } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import Feedback from "../../UI/Forms/FormElements/Feedback";
import { DatePicker } from "../../common/DatePicker";
import { toHHMMSS } from "../../../services/utils";
import { Select } from "../../common/Select";
import { useDisclosure } from "react-use-disclosure";
import { FileUploadModal } from "../../common/FileUploadModal";
import { useStores } from "../../../hooks/useStores";
import { VimeoMediaFolderSelector } from "../../ScreenProgramDetail/VideoForm";
import { observer } from "mobx-react";

type Props = {
  ad?: Ad;
  isSubmitting: boolean;
  onSubmit: (values: EditAd) => void;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
  onCancel: () => void;
  onChangeFolder: (folderId: MediaFolder["projectId"]) => void;
  medias: Media[];
};

const AdForm: FC<Props> = observer(
  ({
    ad,
    isSubmitting,
    onSubmit,
    editable = true,
    isAdmin = false,
    validable,
    onCancel,
    onChangeFolder,
    medias,
  }) => {
    const { programStore, userStore } = useStores();
    const { t } = useTranslation();
    const {
      isOpen: isModalUploadOpen,
      open: openModalUpload,
      close: closeModalUpload,
    } = useDisclosure();
    const [selectedFolder, setSelectedFolder] = useState<{
      label: string;
      value: string;
    }>(
      {} as {
        label: string;
        value: string;
      }
    );
    const [coverImage, setCoverImage] = useState<any>();

    const formik = useFormik({
      initialValues: {
        id: ad?.id || null,
        title: ad?.title || "",
        description: ad?.description || "",
        pictures: ad?.pictures || ({} as Picture),
        externalId: ad?.externalId || "",
        onClickUrl: ad?.onClickUrl || "",
        duration: ad?.duration || undefined,
        validFrom: ad?.validFrom ? new Date(ad?.validFrom) : undefined,
        validUntil: ad?.validUntil ? new Date(ad?.validUntil) : undefined,
        canBeSkipped:
          ad?.canBeSkipped === true ? "canBeSkipped" : "canNotBeSkipped",
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        title: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.titleRequired")
        ),
        externalId: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.urlRequired")
        ),
        onClickUrl: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.onClickUrlRequired")
        ),
        validFrom: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.validFromRequired")
        ),
        validUntil: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.validUntilRequired")
        ),
        canBeSkipped: Yup.string().required(
          t("components.screenAdvertisingDetail.adForm.validUntilRequired")
        ),
      }),
    });

    useEffect(() => {
      // Update Multimedia fields when ExternalId changed

      if (formik.values.externalId) {
        const media = medias.find(
          (media) => media.externalId === formik.values.externalId
        );
        if (media) {
          formik.setFieldValue("pictures", media.pictures);
          formik.setFieldValue("duration", media.duration);
          if (!ad) {
            formik.setFieldValue("title", media.name);
          }
          formik.setFieldValue("description", media.description);
          try {
            setCoverImage(
              media.pictures
                ? media.pictures.sizes
                  ? media.pictures.sizes[6].link
                  : media.pictures[6].link
                : ""
            );
          } catch (_) {}
        } else if (ad) {
          try {
            setCoverImage(
              ad.pictures
                ? ad.pictures.sizes
                  ? ad.pictures.sizes[6].link
                  : ad.pictures[6].link
                : ""
            );
          } catch (_) {}
        }
      }
    }, [ad, formik, formik.values.externalId, medias]);

    const refreshCoverImage = () => {
      const media = medias.find(
        (media) => media.externalId === formik.values.externalId
      );
      if (media) {
        formik.setFieldValue("pictures", media.pictures);

        try {
          setCoverImage(
            //@ts-ignore
            media.pictures.sizes[6].link || media.pictures[6].link
          );
        } catch (_) {}
      }
    };

    const videoOptions = useMemo(() => {
      return medias
        .slice()
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .map((media: Media) => {
          let splittedArray = media.uri.split("/");
          return {
            label: media.createdAt.toLocaleDateString() + " - " + media.name,
            value: splittedArray[splittedArray.length - 1],
          };
        });
    }, [medias]);

    const getSelectedVideoId: any = useCallback(
      (id: any) => {
        return {
          label: medias.find((media: Media) => {
            let splittedArray = media.uri.split("/");
            if (splittedArray[splittedArray.length - 1] === id) return media;
          })?.name,
          value: id,
        };
      },
      [medias]
    );

    const handleUploadPoster = useCallback(
      async (imageUrls: string[]) => {
        formik.setFieldValue("posterUrl", imageUrls[0]);
        closeModalUpload();
      },
      [formik, closeModalUpload]
    );

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <VimeoMediaFolderSelector
            programStore={programStore}
            userStore={userStore}
            t={t}
            onChangeFolder={onChangeFolder}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
          />

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="externalId">
                  {t("components.screenAdvertisingDetail.adForm.url")}
                </Label>
                <Select
                  id="externalId"
                  name="externalId"
                  options={videoOptions}
                  value={getSelectedVideoId(formik.values.externalId)}
                  onChange={(option: any) => {
                    formik.setFieldValue("externalId", option?.value || null);
                  }}
                  isDisabled={!selectedFolder.value || medias.length === 0}
                />
                {formik.errors.externalId && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("externalId").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            {coverImage && (
              <Label htmlFor="title">
                {t("components.screenProgramDetail.videoForm.coverImage")}
              </Label>
            )}
            <img src={coverImage} alt={formik.values.title} />
          </Row>
          {coverImage && ad && (
            <Row mt="10px">
              <Button
                type={"reset"}
                onClick={refreshCoverImage}
                marginRight="10px"
                color="light"
              >
                {t("common.refresh")}
              </Button>
            </Row>
          )}
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="title">
                  {t("components.screenAdvertisingDetail.adForm.title")}
                </Label>
                <Input
                  id="title"
                  name="title"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.titlePlaceholder"
                  )}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  state={formik.errors.title ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("title").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="description">
                  {t("components.screenAdvertisingDetail.adForm.description")}
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.descriptionPlaceholder"
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  showState={true}
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="duration">
                  {t("components.screenAdvertisingDetail.adForm.duration")}
                </Label>
                <Input
                  id="duration"
                  name="duration"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.durationPlaceholder"
                  )}
                  value={
                    (formik.values.duration &&
                      toHHMMSS(formik.values.duration)) ||
                    "00:00:00"
                  }
                  onChange={formik.handleChange}
                  state={formik.errors.duration ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("duration").error}
                  disabled
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="onClickUrl">
                  {t("components.screenAdvertisingDetail.adForm.onClickUrl")}
                </Label>
                <Input
                  id="onClickUrl"
                  name="onClickUrl"
                  placeholder={t(
                    "components.screenAdvertisingDetail.adForm.onClickUrlPlaceholder"
                  )}
                  value={formik.values.onClickUrl}
                  onChange={formik.handleChange}
                  state={formik.errors.onClickUrl ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("onClickUrl").error}
                />
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validFrom">
                  {t("components.screenAdvertisingDetail.adForm.validFrom")}
                </Label>
                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenAdvertisingDetail.adForm.validFromPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validFrom || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validFrom", date || null);
                  }}
                />
                {formik.errors.validFrom && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validFrom").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="validUntil">
                  {t("components.screenAdvertisingDetail.adForm.validUntil")}
                </Label>

                <DatePicker
                  // @ts-ignore
                  size="sm"
                  placeholderText={t(
                    "components.screenAdvertisingDetail.adForm.validUntilPlaceholder"
                  )}
                  dateFormat={"dd/MM/yyyy"}
                  selectedDate={formik.values.validUntil || undefined}
                  onChange={(date) => {
                    formik.setFieldValue("validUntil", date || null);
                  }}
                />
                {formik.errors.validUntil && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("validUntil").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>

          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="highlighted">
                  {t("components.screenAdvertisingDetail.adForm.canBeSkipped")}
                </Label>
                <Radio
                  id="canBeSkipped1"
                  name="canBeSkipped"
                  value={"canBeSkipped"}
                  label={t(
                    "components.screenAdvertisingDetail.adForm.isCanBeSkipped"
                  )}
                  onChange={formik.handleChange}
                  checked={formik.values.canBeSkipped === "canBeSkipped"}
                />
                <Radio
                  id="canBeSkipped2"
                  name="canBeSkipped"
                  value={"canNotBeSkipped"}
                  label={t(
                    "components.screenAdvertisingDetail.adForm.isNotCanBeSkipped"
                  )}
                  onChange={formik.handleChange}
                  checked={formik.values.canBeSkipped === "canNotBeSkipped"}
                />

                {formik.errors.canBeSkipped && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("canBeSkipped").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>

        <FileUploadModal
          isOpen={isModalUploadOpen}
          onClose={closeModalUpload}
          onFileUploaded={handleUploadPoster}
          maxFiles={1}
          accept="image/jpeg, image/png"
        />
      </form>
    );
  }
);

export default AdForm;
